import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Header from '@/components/Header';
import StatusBadge from '@/components/StatusBadge';
import ScoreBar from '@/components/ScoreBar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus, Briefcase, ArrowRight, Upload, X, ExternalLink, Sparkles, Filter, Search, RotateCcw, ChevronDown, ChevronRight, Users, Play, User, Mail, Phone, MapPin, Calendar, Award, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Jobs = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [uploadingZip, setUploadingZip] = useState(false);
  const [matchResults, setMatchResults] = useState(null);
  const [runningMatch, setRunningMatch] = useState(false);
  const [uploadMatchResults, setUploadMatchResults] = useState(null);
  
  // Candidate details dialog
  const [showCandidateDialog, setShowCandidateDialog] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [loadingCandidate, setLoadingCandidate] = useState(false);

  // Expanded matches for each job
  const [expandedMatches, setExpandedMatches] = useState({});
  const [loadingMatches, setLoadingMatches] = useState({});

  // Search and filter states
  const [searchTitle, setSearchTitle] = useState('');
  const [filterSkill, setFilterSkill] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterMinExp, setFilterMinExp] = useState('');
  const [filterMaxExp, setFilterMaxExp] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [minScoreFilter, setMinScoreFilter] = useState(''); // Min score filter for candidates

  useEffect(() => {
    fetchJobs();
  }, []);

  // View candidate details
  const viewCandidateDetails = async (resumeId) => {
    setLoadingCandidate(true);
    setShowCandidateDialog(true);
    try {
      const response = await axios.get(`${API}/resumes/${resumeId}`);
      setSelectedCandidate(response.data);
    } catch (error) {
      console.error('Error fetching candidate details:', error);
      toast.error('Failed to load candidate details');
      setShowCandidateDialog(false);
    } finally {
      setLoadingCandidate(false);
    }
  };

  // Toggle matching resumes for a job
  const toggleMatchingResumes = async (jobId) => {
    if (expandedMatches[jobId]) {
      setExpandedMatches(prev => ({ ...prev, [jobId]: null }));
      return;
    }

    setLoadingMatches(prev => ({ ...prev, [jobId]: true }));
    try {
      const response = await axios.get(`${API}/match/results/${jobId}?min_score=0`);
      setExpandedMatches(prev => ({ ...prev, [jobId]: response.data || [] }));
    } catch (error) {
      console.error('Error fetching matches:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to fetch matching resumes: ${errorMsg}`);
      setExpandedMatches(prev => ({ ...prev, [jobId]: [] }));
    } finally {
      setLoadingMatches(prev => ({ ...prev, [jobId]: false }));
    }
  };

  // Run matching for all jobs
  const runMatching = async () => {
    setRunningMatch(true);
    toast.info('Running matching algorithm... This may take a moment.');
    try {
      const response = await axios.post(`${API}/match/run`, {}, { timeout: 300000 }); // 5 min timeout
      toast.success(`Matching completed! ${response.data.matches_created} matches created.`);
      fetchJobs();
      // Clear expanded matches to force refresh
      setExpandedMatches({});
    } catch (error) {
      console.error('Error running matching:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to run matching: ${errorMsg}`);
    } finally {
      setRunningMatch(false);
    }
  };

  // Get unique values for filter dropdowns
  const filterOptions = useMemo(() => {
    const skills = new Set();
    const locations = new Set();
    
    jobs.forEach(job => {
      job.required_skills?.forEach(s => skills.add(s));

      if (job.location) locations.add(job.location);
    });
    
    return {
      skills: Array.from(skills).sort(),
      locations: Array.from(locations).sort()
    };
  }, [jobs]);

  // Filtered jobs
  const filteredJobs = useMemo(() => {
    return jobs.filter(job => {
      // Filter by status
      if (filter !== 'all' && job.status !== filter) {
        return false;
      }
      
      // Search by title
      if (searchTitle && !job.title?.toLowerCase().includes(searchTitle.toLowerCase())) {
        return false;
      }
      
      // Filter by skill
      if (filterSkill && !job.required_skills?.some(s => s.toLowerCase().includes(filterSkill.toLowerCase()))) {
        return false;
      }
      
      // Filter by location
      if (filterLocation) {
        const jobLocation = (job.location || '').toLowerCase();
        if (!jobLocation.includes(filterLocation.toLowerCase())) {
          return false;
        }
      }
      
      // Filter by experience range
      const minExp = job.min_experience || 0;
      if (filterMinExp && minExp < parseFloat(filterMinExp)) {
        return false;
      }
      if (filterMaxExp && minExp > parseFloat(filterMaxExp)) {
        return false;
      }
      
      return true;
    });
  }, [jobs, filter, searchTitle, filterSkill, filterLocation, filterMinExp, filterMaxExp]);

  const clearFilters = () => {
    setSearchTitle('');
    setFilterSkill('');
    setFilterLocation('');
    setFilterMinExp('');
    setFilterMaxExp('');
    setFilter('all');
    setMinScoreFilter('');
  };

  const hasActiveFilters = searchTitle || filterSkill || filterLocation || filterMinExp || filterMaxExp || minScoreFilter;

  // Filter matches by min score for display
  const getFilteredMatches = (matches) => {
    if (!matches || !minScoreFilter) return matches;
    const minScore = parseFloat(minScoreFilter);
    if (isNaN(minScore)) return matches;
    return matches.filter(m => m.total_score >= minScore);
  };

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/jobs`);
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to fetch job descriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    try {
      // Use the new endpoint that returns matches immediately
      const response = await axios.post(`${API}/jobs/upload-with-matches`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000 // 5 minute timeout for AI parsing
      });
      
      const { jobs: uploadedJobs, match_results, total_jobs, total_matches } = response.data;
      
      toast.success(`Successfully uploaded ${total_jobs} job(s) with ${total_matches} matching candidate(s)!`);
      
      // Show match results popup if there are matches
      if (match_results && match_results.length > 0 && match_results.some(r => r.matches.length > 0)) {
        setUploadMatchResults(match_results);
      }
      
      fetchJobs();
    } catch (error) {
      console.error('Error uploading JDs:', error);
      toast.error('Failed to upload job descriptions');
    } finally {
      setUploading(false);
    }
  };

  const handleZipUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (15MB limit)
    const maxSize = 15 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 15MB');
      return;
    }

    if (!file.name.toLowerCase().endsWith('.zip')) {
      toast.error('Please upload a ZIP file');
      return;
    }

    setUploadingZip(true);
    toast.info('Processing ZIP file... This may take a moment for large files.');
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload-zip`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000 // 5 minute timeout
      });
      
      const { resumes_uploaded, jds_uploaded, matching_status, note } = response.data;
      
      toast.success(`Processed: ${resumes_uploaded} resume(s), ${jds_uploaded} JD(s)`);
      
      fetchJobs();
      
      // Background matching notification
      if (matching_status === 'background_started') {
        toast.info('🔄 Matching is running in the background. Refresh the page in a few moments to see updated matches.');
      }
      
      if (note) {
        toast.info(note);
      }
    } catch (error) {
      console.error('Error uploading ZIP:', error);
      if (error.response?.status === 413) {
        toast.error('File too large. Maximum size is 15MB');
      } else if (error.code === 'ECONNABORTED') {
        toast.error('Upload timed out. Try a smaller file.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to process ZIP file');
      }
    } finally {
      setUploadingZip(false);
      event.target.value = ''; // Reset file input
    }
  };

  const autoMatchJobs = async (jdIds) => {
    try {
      const resumesResponse = await axios.get(`${API}/resumes`);
      if (resumesResponse.data.length === 0) {
        toast.info('No resumes to match against');
        return;
      }
      
      toast.info('Finding matching candidates...');
      await axios.post(`${API}/match/run`);
      
      // Fetch match results for the uploaded JDs
      const allMatches = [];
      for (const jdId of jdIds) {
        const job = await axios.get(`${API}/jobs/${jdId}`);
        const matchRes = await axios.get(`${API}/match/results/${jdId}?min_score=50`);
        if (matchRes.data.length > 0) {
          allMatches.push({
            job: job.data,
            matches: matchRes.data
          });
        }
      }
      
      if (allMatches.length > 0) {
        setMatchResults(allMatches);
        toast.success(`Found matches for ${allMatches.length} job(s)!`);
      } else {
        toast.info('No matches found with score ≥ 50%');
      }
    } catch (error) {
      console.error('Error matching jobs:', error);
    }
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 via-brand-orange/5 to-brand-cyan/5 min-h-screen">
      <Header
        title="Job Descriptions"
        subtitle={`${jobs.length} job description(s)`}
        action={
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => document.getElementById('zip-upload-jobs').click()}
              disabled={uploadingZip}
              className="border-brand-orange/30 hover:border-brand-orange hover:bg-brand-orange/10"
              data-testid="upload-zip-jobs-button"
            >
              <Upload size={16} className="mr-2 text-brand-orange" />
              {uploadingZip ? 'Processing ZIP...' : 'Upload ZIP'}
            </Button>
            <input
              id="zip-upload-jobs"
              type="file"
              accept=".zip"
              onChange={handleZipUpload}
              className="hidden"
              data-testid="zip-upload-jobs-input"
            />
            <Button
              variant="outline"
              onClick={() => document.getElementById('jd-file-upload').click()}
              disabled={uploading}
              className="border-brand-cyan/30 hover:border-brand-cyan hover:bg-brand-cyan/10"
              data-testid="upload-jds-button"
            >
              <Upload size={16} className="mr-2 text-brand-cyan" />
              {uploading ? 'Uploading...' : 'Upload JDs'}
            </Button>
            <input
              id="jd-file-upload"
              type="file"
              multiple
              accept=".pdf,.docx,.txt"
              onChange={handleBulkUpload}
              className="hidden"
              data-testid="jd-file-upload-input"
            />
            <Button
              onClick={() => navigate('/jobs/new')}
              className="bg-gradient-to-r from-brand-blue to-brand-cyan text-white hover:from-brand-cyan hover:to-brand-blue shadow-lg"
              data-testid="create-new-job-button"
            >
              <Plus size={16} className="mr-2" />
              Create New Job
            </Button>
            <Button
              onClick={runMatching}
              disabled={runningMatch}
              className="bg-gradient-to-r from-brand-magenta to-pink-500 text-white hover:from-pink-500 hover:to-brand-magenta shadow-lg"
              data-testid="run-matching-button"
            >
              <Play size={16} className="mr-2" />
              {runningMatch ? 'Running...' : 'Run Matching'}
            </Button>
          </div>
        }
      />

      <div className="p-8">
        {matchResults && (
          <Card className="bg-white/90 backdrop-blur-sm border-2 border-brand-cyan/20 p-6 mb-6 shadow-lg rounded-xl" data-testid="match-results-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-cyan to-brand-blue flex items-center justify-center">
                  <Sparkles size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold font-heading text-slate-900">Matching Candidates Found</h2>
                  <p className="text-sm text-slate-500">Candidates that match your uploaded jobs</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setMatchResults(null)} className="hover:bg-red-50 hover:text-red-500" data-testid="close-match-results">
                <X size={20} />
              </Button>
            </div>
            
            <div className="space-y-4">
              {matchResults.map((result, idx) => (
                <div key={idx} className="border-2 border-slate-100 rounded-xl p-4 hover:border-brand-cyan/30 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        idx === 0 ? 'bg-brand-cyan' : 
                        idx === 1 ? 'bg-brand-orange' : 
                        'bg-brand-magenta'
                      }`} />
                      <h3 className="font-bold text-slate-900">{result.job.title}</h3>
                      <StatusBadge status={result.job.status} />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/jobs/${result.job.id}`)}
                      className="border-brand-cyan/30 text-brand-cyan hover:bg-brand-cyan hover:text-white"
                      data-testid={`view-job-${result.job.id}`}
                    >
                      <ExternalLink size={14} className="mr-1" />
                      View All Matches
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    {result.matches.slice(0, 5).map((match) => (
                      <div key={match.resume_id} className="bg-gradient-to-r from-slate-50 to-brand-cyan/5 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-semibold text-slate-900">{match.resume_name}</p>
                          <span className={`font-mono font-bold ${
                            match.total_score >= 80 ? 'text-green-600' :
                            match.total_score >= 60 ? 'text-brand-yellow' :
                            'text-brand-orange'
                          }`}>
                            {match.total_score.toFixed(1)}%
                          </span>
                        </div>
                        <ScoreBar score={match.total_score} category={match.category} />
                        <p className="text-xs text-slate-600 mt-2">
                          <span className="font-medium text-brand-blue">{match.category}</span> • 
                          Skills: <span className="text-brand-cyan">{match.skill_score.toFixed(1)}%</span> • 
                          Experience: <span className="text-brand-orange">{match.experience_score.toFixed(1)}%</span> • 
                          Tools: <span className="text-brand-magenta">{match.tools_score.toFixed(1)}%</span>
                        </p>
                      </div>
                    ))}
                    {result.matches.length > 5 && (
                      <p className="text-sm text-slate-500 text-center pt-2">
                        +{result.matches.length - 5} more candidates
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Search and Filter Section */}
        <Card className="bg-white/90 backdrop-blur-sm border-2 border-brand-orange/10 p-4 mb-6 shadow-sm rounded-xl" data-testid="job-filter-section">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search by Title */}
            <div className="flex-1 min-w-[200px] max-w-[300px]">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search by job title..."
                  value={searchTitle}
                  onChange={(e) => setSearchTitle(e.target.value)}
                  className="pl-9 border-slate-200 focus:border-brand-orange"
                  data-testid="search-title-input"
                />
              </div>
            </div>

            {/* Status Filter Buttons */}
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant={filter === 'all' ? 'default' : 'outline'}
                onClick={() => setFilter('all')}
                className={filter === 'all' ? 'bg-gradient-to-r from-brand-blue to-brand-cyan text-white' : 'border-slate-200'}
                data-testid="filter-all-button"
              >
                All
              </Button>
              <Button
                size="sm"
                variant={filter === 'ACTIVE' ? 'default' : 'outline'}
                onClick={() => setFilter('ACTIVE')}
                className={filter === 'ACTIVE' ? 'bg-green-500 text-white' : 'border-slate-200'}
                data-testid="filter-active-button"
              >
                Active
              </Button>
              <Button
                size="sm"
                variant={filter === 'CLOSED' ? 'default' : 'outline'}
                onClick={() => setFilter('CLOSED')}
                className={filter === 'CLOSED' ? 'bg-slate-500 text-white' : 'border-slate-200'}
                data-testid="filter-closed-button"
              >
                Closed
              </Button>
              <Button
                size="sm"
                variant={filter === 'ON_HOLD' ? 'default' : 'outline'}
                onClick={() => setFilter('ON_HOLD')}
                className={filter === 'ON_HOLD' ? 'bg-brand-orange text-white' : 'border-slate-200'}
                data-testid="filter-onhold-button"
              >
                On Hold
              </Button>
            </div>

            {/* More Filters Toggle */}
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`${showFilters ? 'bg-brand-orange/10 border-brand-orange text-brand-orange' : 'border-slate-200'}`}
              data-testid="toggle-job-filters-button"
            >
              <Filter size={16} className="mr-2" />
              More {hasActiveFilters && <span className="ml-1 px-1.5 py-0.5 bg-brand-orange text-white text-xs rounded-full">{[filterSkill, filterLocation, filterMinExp, filterMaxExp, minScoreFilter].filter(Boolean).length}</span>}
            </Button>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                onClick={clearFilters}
                className="text-slate-500 hover:text-red-500"
                data-testid="clear-job-filters-button"
              >
                <RotateCcw size={16} className="mr-1" />
                Clear
              </Button>
            )}

            {/* Results Count */}
            <div className="ml-auto text-sm text-slate-500">
              Showing <span className="font-bold text-brand-orange">{filteredJobs.length}</span> of {jobs.length} jobs
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Filter by Skill */}
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Filter by Required Skill</label>
                <Input
                  placeholder="e.g., Python, Sales..."
                  value={filterSkill}
                  onChange={(e) => setFilterSkill(e.target.value)}
                  className="border-slate-200 focus:border-brand-cyan"
                  data-testid="filter-job-skill-input"
                />
              </div>

              {/* Filter by Location */}
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Filter by Location</label>
                <Select value={filterLocation || "all"} onValueChange={(val) => setFilterLocation(val === "all" ? "" : val)}>
                  <SelectTrigger className="border-slate-200">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    {filterOptions.locations.map(loc => (
                      <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Min Experience */}
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Min Experience Required</label>
                <Input
                  type="number"
                  min="0"
                  placeholder="0"
                  value={filterMinExp}
                  onChange={(e) => setFilterMinExp(e.target.value)}
                  className="border-slate-200 focus:border-brand-orange"
                  data-testid="filter-job-min-exp-input"
                />
              </div>

              {/* Max Experience */}
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Max Experience Required</label>
                <Input
                  type="number"
                  min="0"
                  placeholder="50"
                  value={filterMaxExp}
                  onChange={(e) => setFilterMaxExp(e.target.value)}
                  className="border-slate-200 focus:border-brand-orange"
                  data-testid="filter-job-max-exp-input"
                />
              </div>

              {/* Min Match Score Filter */}
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Min Candidate Score (%)</label>
                <Select value={minScoreFilter || "all"} onValueChange={(val) => setMinScoreFilter(val === "all" ? "" : val)}>
                  <SelectTrigger className="border-slate-200" data-testid="filter-min-score-select">
                    <SelectValue placeholder="All Scores" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Scores</SelectItem>
                    <SelectItem value="50">≥ 50% (Good Fit)</SelectItem>
                    <SelectItem value="60">≥ 60% (Strong Fit)</SelectItem>
                    <SelectItem value="70">≥ 70% (Very Strong)</SelectItem>
                    <SelectItem value="80">≥ 80% (Excellent)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </Card>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center gap-3 text-slate-500">
              <div className="w-6 h-6 border-2 border-brand-cyan border-t-transparent rounded-full animate-spin"></div>
              Loading job descriptions...
            </div>
          </div>
        ) : jobs.length === 0 ? (
          <Card className="bg-white/80 backdrop-blur-sm border-2 border-brand-orange/20 p-12 text-center rounded-xl" data-testid="empty-jobs-state">
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-brand-orange/20 to-brand-yellow/20 rounded-2xl flex items-center justify-center mb-4">
              <Briefcase size={40} className="text-brand-orange" />
            </div>
            <p className="text-slate-600 mb-4 font-medium">No job descriptions found</p>
            <Button
              onClick={() => navigate('/jobs/new')}
              className="bg-gradient-to-r from-brand-orange to-brand-yellow text-white hover:from-brand-yellow hover:to-brand-orange"
              data-testid="create-first-job-button"
            >
              Create Your First Job
            </Button>
          </Card>
        ) : filteredJobs.length === 0 ? (
          <Card className="bg-white/80 backdrop-blur-sm border-2 border-amber-200 p-12 text-center rounded-xl" data-testid="no-job-filter-results">
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl flex items-center justify-center mb-4">
              <Search size={40} className="text-amber-500" />
            </div>
            <p className="text-slate-600 mb-2 font-medium">No jobs match your filters</p>
            <p className="text-sm text-slate-500 mb-4">Try adjusting your search criteria</p>
            <Button
              onClick={clearFilters}
              variant="outline"
              className="border-amber-300 text-amber-600 hover:bg-amber-50"
            >
              <RotateCcw size={16} className="mr-2" />
              Clear All Filters
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredJobs.map((job, index) => (
              <Card
                key={job.id}
                data-testid={`job-card-${job.id}`}
                className="bg-white/80 backdrop-blur-sm border-2 border-slate-100 hover:border-brand-cyan/30 transition-all duration-300 rounded-xl overflow-hidden"
              >
                <div 
                  className="p-6 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMatchingResumes(job.id);
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleMatchingResumes(job.id);
                          }}
                          className="text-slate-400 hover:text-brand-cyan transition-colors"
                        >
                          {expandedMatches[job.id] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                        </button>
                        <div className={`w-3 h-3 rounded-full ${
                          index % 4 === 0 ? 'bg-brand-blue' : 
                          index % 4 === 1 ? 'bg-brand-cyan' : 
                          index % 4 === 2 ? 'bg-brand-orange' : 
                          'bg-brand-magenta'
                        }`} />
                        <h3 
                          className="text-lg font-bold font-heading text-slate-900 hover:text-brand-blue transition-colors cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/jobs/${job.id}`);
                          }}
                        >
                          {job.title}
                        </h3>
                        <StatusBadge status={job.status} />
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 ml-8">
                        <div className="bg-brand-blue/5 rounded-lg p-2">
                          <p className="text-xs text-slate-500">Required Skills</p>
                          <p className="font-bold text-brand-blue">{job.required_skills?.length || 0}</p>
                        </div>
                        {job.min_experience && (
                          <div className="bg-brand-cyan/5 rounded-lg p-2">
                            <p className="text-xs text-slate-500">Min Experience</p>
                            <p className="font-bold text-brand-cyan">{job.min_experience}y</p>
                          </div>
                        )}
                        {job.location && (
                          <div className="bg-brand-orange/5 rounded-lg p-2">
                            <p className="text-xs text-slate-500">Location</p>
                            <p className="font-bold text-brand-orange text-sm">{job.location}</p>
                          </div>
                        )}
                        <div className="bg-brand-magenta/5 rounded-lg p-2">
                          <p className="text-xs text-slate-500">Matches</p>
                          <p className="font-bold text-brand-magenta">{job.match_count || 0}</p>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 ml-8">
                        {job.required_skills?.slice(0, 5).map((skill, idx) => (
                          <span
                            key={idx}
                            className={`px-3 py-1 text-xs rounded-full font-medium ${
                              idx % 3 === 0 ? 'bg-brand-blue/10 text-brand-blue' : 
                              idx % 3 === 1 ? 'bg-brand-cyan/10 text-brand-cyan' : 
                              'bg-brand-orange/10 text-brand-orange'
                            }`}
                          >
                            {skill}
                          </span>
                        ))}
                        {job.required_skills?.length > 5 && (
                          <span className="px-3 py-1 bg-slate-100 text-slate-600 text-xs rounded-full font-medium">
                            +{job.required_skills.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/jobs/${job.id}`);
                      }}
                      className="border-brand-cyan/30 text-brand-cyan hover:bg-brand-cyan hover:text-white"
                    >
                      <ExternalLink size={14} className="mr-1" />
                      Details
                    </Button>
                  </div>
                </div>

                {/* Expanded Matching Resumes */}
                {expandedMatches[job.id] !== undefined && expandedMatches[job.id] !== null && (
                  <div className="bg-gradient-to-r from-brand-cyan/5 to-brand-magenta/5 border-t border-slate-100 px-6 py-4">
                    <div className="flex items-start gap-2 ml-6">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-magenta to-pink-400 flex items-center justify-center flex-shrink-0">
                        <Users size={14} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-slate-900 mb-3">
                          Matching Candidates {minScoreFilter ? `(Score ≥ ${minScoreFilter}%)` : '(All Scores)'}
                        </p>
                        {loadingMatches[job.id] ? (
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <div className="w-4 h-4 border-2 border-brand-cyan border-t-transparent rounded-full animate-spin"></div>
                            Loading matches...
                          </div>
                        ) : (() => {
                          const filteredMatchList = getFilteredMatches(expandedMatches[job.id]);
                          return filteredMatchList.length === 0 ? (
                            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
                              <p className="text-sm text-amber-800 font-medium mb-1">
                                {minScoreFilter ? `No candidates with score ≥ ${minScoreFilter}%` : 'No matches found'}
                              </p>
                              <p className="text-xs text-amber-700">
                                {minScoreFilter 
                                  ? 'Try lowering the minimum score filter or upload more resumes.'
                                  : 'Upload more resumes or run matching to find candidates.'}
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {filteredMatchList.slice(0, 10).map((match, idx) => (
                                <div
                                  key={match.resume_id}
                                  className="bg-white border-2 border-slate-100 rounded-xl p-3 hover:border-brand-cyan/30 transition-all"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                      idx % 3 === 0 ? 'bg-brand-cyan' : 
                                      idx % 3 === 1 ? 'bg-brand-orange' : 
                                      'bg-brand-magenta'
                                    }`} />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            viewCandidateDetails(match.resume_id);
                                          }}
                                          className="font-semibold text-brand-blue text-sm truncate max-w-[180px] hover:underline cursor-pointer text-left"
                                          data-testid={`view-candidate-${match.resume_id}`}
                                        >
                                          {match.resume_name}
                                        </button>
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${
                                          match.total_score >= 80 ? 'bg-green-100 text-green-700' :
                                          match.total_score >= 60 ? 'bg-yellow-100 text-yellow-700' :
                                          match.total_score >= 50 ? 'bg-orange-100 text-orange-700' :
                                          'bg-red-100 text-red-700'
                                        }`}>
                                          {match.category}
                                        </span>
                                        {match.resume_experience && (
                                          <span className="px-1.5 py-0.5 bg-brand-blue/10 text-brand-blue rounded text-[10px] font-medium flex-shrink-0">
                                            {match.resume_experience}y
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-[10px] text-slate-500 mt-0.5">
                                        Skills: {match.skill_score?.toFixed(0) || 0}% • Exp: {match.experience_score?.toFixed(0) || 0}%
                                      </p>
                                    </div>
                                    <span className={`font-mono text-lg font-bold flex-shrink-0 ${
                                      match.total_score >= 80 ? 'text-green-600' :
                                      match.total_score >= 60 ? 'text-yellow-600' :
                                      match.total_score >= 50 ? 'text-orange-600' :
                                      'text-red-600'
                                    }`}>
                                      {match.total_score?.toFixed(0) || 0}%
                                    </span>
                                  </div>
                                </div>
                              ))}
                              {filteredMatchList.length > 10 && (
                                <p className="text-sm text-slate-500 text-center pt-2">
                                  +{filteredMatchList.length - 10} more candidates. 
                                  <button 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/jobs/${job.id}`);
                                    }}
                                    className="text-brand-cyan hover:underline ml-1"
                                  >
                                    View all
                                  </button>
                                </p>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Upload Match Results Dialog for JD uploads */}
      {uploadMatchResults && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden shadow-2xl">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-magenta to-pink-400 flex items-center justify-center">
                  <Users size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Matching Candidates Found!</h2>
                  <p className="text-sm text-slate-500">Candidates that match your uploaded job(s)</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setUploadMatchResults(null)}
                className="hover:bg-red-50 hover:text-red-500"
              >
                <X size={20} />
              </Button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-100px)]">
              <div className="space-y-6">
                {uploadMatchResults.map((result, idx) => (
                  <div key={idx} className="border-2 border-slate-100 rounded-xl p-4 hover:border-brand-magenta/30 transition-colors">
                    <div className="flex items-center gap-3 mb-4 pb-3 border-b border-slate-100">
                      <div className={`w-3 h-3 rounded-full ${
                        idx % 3 === 0 ? 'bg-brand-blue' : 
                        idx % 3 === 1 ? 'bg-brand-orange' : 
                        'bg-brand-magenta'
                      }`} />
                      <div className="flex-1">
                        <h3 className="font-bold text-slate-900">{result.jd_title}</h3>
                        <p className="text-sm text-slate-500">{result.matches.length} matching candidate(s)</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setUploadMatchResults(null);
                          navigate(`/jobs/${result.jd_id}`);
                        }}
                        className="border-brand-cyan/30 text-brand-cyan hover:bg-brand-cyan hover:text-white"
                      >
                        <ExternalLink size={14} className="mr-1" />
                        View Job
                      </Button>
                    </div>
                    
                    {result.matches.length === 0 ? (
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
                        <p className="text-sm text-amber-800 font-medium">No matches found (score ≥ 40%)</p>
                        <p className="text-xs text-amber-700 mt-1">Upload more resumes or adjust job requirements.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {result.matches.slice(0, 10).map((match, matchIdx) => (
                          <div key={matchIdx} className="bg-gradient-to-r from-slate-50 to-brand-magenta/5 rounded-xl p-4 border border-slate-100">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <div className={`w-2 h-2 rounded-full ${
                                    matchIdx % 3 === 0 ? 'bg-brand-cyan' : 
                                    matchIdx % 3 === 1 ? 'bg-brand-orange' : 
                                    'bg-brand-magenta'
                                  }`} />
                                  <p className="font-semibold text-slate-900">{match.resume_name}</p>
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                    match.total_score >= 80 ? 'bg-green-100 text-green-700' :
                                    match.total_score >= 60 ? 'bg-yellow-100 text-yellow-700' :
                                    match.total_score >= 50 ? 'bg-orange-100 text-orange-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>
                                    {match.category}
                                  </span>
                                  {match.resume_experience && (
                                    <span className="px-2 py-0.5 bg-brand-blue/10 text-brand-blue rounded-full text-xs font-medium">
                                      {match.resume_experience} yrs exp
                                    </span>
                                  )}
                                </div>
                                <ScoreBar score={match.total_score} category={match.category} />
                                <p className="text-xs text-slate-600 mt-2">
                                  Skills: <span className="text-brand-cyan font-medium">{match.skill_score}%</span> • 
                                  Experience: <span className="text-brand-orange font-medium">{match.experience_score}%</span>
                                </p>
                              </div>
                              <div className="ml-4">
                                <span className={`font-mono text-2xl font-bold ${
                                  match.total_score >= 80 ? 'text-green-600' :
                                  match.total_score >= 60 ? 'text-yellow-600' :
                                  match.total_score >= 50 ? 'text-orange-600' :
                                  'text-red-600'
                                }`}>
                                  {match.total_score}%
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                        {result.matches.length > 10 && (
                          <p className="text-sm text-slate-500 text-center pt-2">
                            +{result.matches.length - 10} more matching candidates
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Candidate Details Dialog */}
      <Dialog open={showCandidateDialog} onOpenChange={setShowCandidateDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-blue to-brand-cyan flex items-center justify-center">
                <User size={20} className="text-white" />
              </div>
              <div>
                <span className="text-xl">{selectedCandidate?.name || 'Candidate Details'}</span>
                <p className="text-sm font-normal text-slate-500">Resume and profile information</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {loadingCandidate ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-3 border-brand-cyan border-t-transparent rounded-full animate-spin"></div>
              <span className="ml-3 text-slate-600">Loading candidate details...</span>
            </div>
          ) : selectedCandidate && (
            <div className="space-y-6 mt-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gradient-to-r from-brand-blue/5 to-brand-cyan/5 rounded-xl p-4 border border-brand-blue/10">
                  <div className="flex items-center gap-2 text-brand-blue mb-2">
                    <User size={16} />
                    <span className="font-semibold text-sm">Name</span>
                  </div>
                  <p className="text-slate-900 font-medium">{selectedCandidate.name || 'Not specified'}</p>
                </div>
                <div className="bg-gradient-to-r from-brand-cyan/5 to-brand-magenta/5 rounded-xl p-4 border border-brand-cyan/10">
                  <div className="flex items-center gap-2 text-brand-cyan mb-2">
                    <Mail size={16} />
                    <span className="font-semibold text-sm">Email</span>
                  </div>
                  <p className="text-slate-900 font-medium">{selectedCandidate.email || 'Not specified'}</p>
                </div>
                <div className="bg-gradient-to-r from-brand-orange/5 to-brand-yellow/5 rounded-xl p-4 border border-brand-orange/10">
                  <div className="flex items-center gap-2 text-brand-orange mb-2">
                    <Phone size={16} />
                    <span className="font-semibold text-sm">Phone</span>
                  </div>
                  <p className="text-slate-900 font-medium">{selectedCandidate.phone || 'Not specified'}</p>
                </div>
                <div className="bg-gradient-to-r from-brand-magenta/5 to-pink-50 rounded-xl p-4 border border-brand-magenta/10">
                  <div className="flex items-center gap-2 text-brand-magenta mb-2">
                    <MapPin size={16} />
                    <span className="font-semibold text-sm">Location</span>
                  </div>
                  <p className="text-slate-900 font-medium">{selectedCandidate.location || selectedCandidate.current_location || 'Not specified'}</p>
                </div>
              </div>

              {/* Experience & Salary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 text-center">
                  <div className="flex items-center justify-center gap-2 text-brand-blue mb-2">
                    <Calendar size={16} />
                    <span className="font-semibold text-sm">Experience</span>
                  </div>
                  <p className="text-2xl font-bold text-brand-blue">{selectedCandidate.experience_years || 0}</p>
                  <p className="text-xs text-slate-500">years</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 text-center">
                  <div className="flex items-center justify-center gap-2 text-green-600 mb-2">
                    <span className="font-semibold text-sm">Current Salary</span>
                  </div>
                  <p className="text-lg font-bold text-green-600">{selectedCandidate.current_salary || 'N/A'}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 text-center">
                  <div className="flex items-center justify-center gap-2 text-brand-orange mb-2">
                    <span className="font-semibold text-sm">Expected Salary</span>
                  </div>
                  <p className="text-lg font-bold text-brand-orange">{selectedCandidate.expected_salary || 'N/A'}</p>
                </div>
              </div>

              {/* Skills */}
              {selectedCandidate.skills && selectedCandidate.skills.length > 0 && (
                <div className="border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-brand-cyan mb-3">
                    <Award size={16} />
                    <span className="font-bold">Skills</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedCandidate.skills.map((skill, idx) => (
                      <span 
                        key={idx} 
                        className={`px-3 py-1 rounded-full text-sm font-medium ${
                          idx % 4 === 0 ? 'bg-brand-cyan/10 text-brand-cyan' :
                          idx % 4 === 1 ? 'bg-brand-orange/10 text-brand-orange' :
                          idx % 4 === 2 ? 'bg-brand-magenta/10 text-brand-magenta' :
                          'bg-brand-blue/10 text-brand-blue'
                        }`}
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Education */}
              {selectedCandidate.education && (
                <div className="border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-brand-blue mb-3">
                    <Award size={16} />
                    <span className="font-bold">Education</span>
                  </div>
                  <p className="text-slate-700">{selectedCandidate.education}</p>
                </div>
              )}

              {/* Resume Text */}
              {selectedCandidate.raw_text && (
                <div className="border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-slate-700 mb-3">
                    <FileText size={16} />
                    <span className="font-bold">Resume Content</span>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg text-sm whitespace-pre-wrap font-mono text-slate-700 max-h-64 overflow-y-auto">
                    {selectedCandidate.raw_text}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Jobs;